import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useState } from 'react';
import PropTypes from '../../../node_modules/prop-types/index.js';
import i18n from '../../lang/i18n/index.js';
import { safeRest, hasErrors, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import Errors from '../Errors/Errors.js';
import Label from '../Label/Label.js';
import { DEFAULT_ROWS } from './constants.js';
import { Root, LabelRow, TooltipContainer, InputArea, MaxLength } from './styledComponents.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';
import useDebounce from '../../hooks/useDebounce.js';
import componentWithName from '../../prop-types/componentWithName.js';

var _excluded = ["id", "value", "label", "errors", "callouts", "tooltip", "maxLength", "rows", "ariaLabel", "ariaRequired", "ariaDescribedBy", "disabled", "autoFocus", "placeholder", "required", "customStyle", "onChange"];
var TextArea = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var controlledId = _ref.id,
      value = _ref.value,
      label = _ref.label,
      errors = _ref.errors,
      callouts = _ref.callouts,
      tooltip = _ref.tooltip,
      maxLength = _ref.maxLength,
      rows = _ref.rows,
      ariaLabel = _ref.ariaLabel,
      ariaRequired = _ref.ariaRequired,
      ariaDescribedBy = _ref.ariaDescribedBy,
      disabled = _ref.disabled,
      autoFocus = _ref.autoFocus,
      placeholder = _ref.placeholder,
      required = _ref.required,
      customStyle = _ref.customStyle,
      onChange = _ref.onChange,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useDerivedState = useDerivedState(useId('textarea-'), controlledId),
      _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
      id = _useDerivedState2[0];

  var _safeCustomStyle = safeCustomStyle(customStyle),
      rootStyle = _safeCustomStyle.rootStyle,
      inputAreaStyle = _safeCustomStyle.inputAreaStyle,
      maxLengthStyle = _safeCustomStyle.maxLengthStyle;

  var safeProps = safeRest(rest);
  var lang = useLang();
  var theme = useTheme();

  var _useState = useState(value ? value.length : 0),
      _useState2 = _slicedToArray(_useState, 2),
      characterCount = _useState2[0],
      setCharacterCount = _useState2[1];

  var hasError = hasErrors(errors);
  /**
   * Rather than reading out the character count every time the text input changes, wait a second and then read out
   * the newest readout instead. This avoids repeated clipping of the readout when the user is typing quickly.
   */

  var charCountAria = useDebounce(characterCount, 1000);

  var handleTextAreaChange = function handleTextAreaChange(event) {
    var newValue = event.target.value;
    setCharacterCount(newValue ? newValue.length : 0);
    return handle(onChange)(event);
  };

  var renderMaxLength = function renderMaxLength() {
    if (maxLength && Number.parseInt(maxLength, 10) > 0) {
      return /*#__PURE__*/React.createElement(MaxLength, {
        theme: theme,
        role: "status",
        "aria-label": i18n[lang].textArea.maxLengthAriaLabelTemplate(charCountAria, maxLength),
        "aria-live": "polite",
        "data-testid": "textarea-maxlength",
        maxLengthStyle: maxLengthStyle
      }, /*#__PURE__*/React.createElement("span", {
        "aria-hidden": true
      }, "".concat(characterCount, "/").concat(maxLength)));
    }

    return null;
  };

  return /*#__PURE__*/React.createElement(Root, {
    rootStyle: rootStyle,
    theme: theme
  }, /*#__PURE__*/React.createElement(LabelRow, null, label && /*#__PURE__*/React.createElement(Label, {
    htmlFor: id
  }, label, "\xA0", tooltip && /*#__PURE__*/React.createElement(TooltipContainer, null, tooltip)), renderMaxLength()), /*#__PURE__*/React.createElement(InputArea, _extends({}, safeProps, {
    id: id,
    "data-testid": "textarea-input",
    hasError: hasError,
    theme: theme,
    rows: rows,
    maxLength: maxLength,
    onChange: handleTextAreaChange,
    "aria-label": ariaLabel,
    "aria-required": ariaRequired,
    "aria-describedby": hasError ? "".concat(id, "-errors") : ariaDescribedBy,
    placeholder: placeholder,
    value: value,
    disabled: disabled,
    autoFocus: autoFocus,
    required: required,
    inputAreaStyle: inputAreaStyle,
    ref: ref
  })), /*#__PURE__*/React.createElement(Errors, {
    id: id,
    callouts: callouts,
    errors: errors
  }));
});
process.env.NODE_ENV !== "production" ? TextArea.reduxFormProps = {
  inputProp: 'value',
  inputType: 'string'
} : void 0;
process.env.NODE_ENV !== "production" ? TextArea.propTypes = {
  /** id of the input element */
  id: PropTypes.string,

  /** The content of the text area */
  value: PropTypes.string,

  /** The label text for the text area */
  label: PropTypes.node,

  /** error array */
  errors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    // error id
    msg: PropTypes.any,
    // localized error text
    isError: PropTypes.bool // error condition

  })),

  /** sets aria-live to 'polite' and role to 'alert' on error message block  */
  callouts: PropTypes.bool,

  /** [Tooltip](/?path=/docs/components-tooltip) displayed beside the label */
  tooltip: componentWithName('Tooltip'),

  /** The maximum character limit; corresponds to HTML textarea maxlength prop; falsy means no limit */
  maxLength: PropTypes.number,

  /** The number of lines to display (e.g. the height of the input area); corresponds to HTML textarea rows prop */
  rows: PropTypes.number,

  /** global attribute aria-label */
  ariaLabel: PropTypes.string,

  /** global attribute aria-required */
  ariaRequired: PropTypes.bool,

  /** DOM element ID for aria-describedBy on input */
  ariaDescribedBy: PropTypes.string,

  /** HTML 5 disabled attribute */
  disabled: PropTypes.bool,

  /** HTML 5 autofocus attribute */
  autoFocus: PropTypes.bool,

  /** HTML 5 placeholder attribute */
  placeholder: PropTypes.string,

  /** Whether a value is required or not */
  required: PropTypes.bool,

  /** override for styling */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    inputAreaStyle: PropTypes.object,
    maxLengthStyle: PropTypes.object
  }),

  /** change handler for input element */
  onChange: PropTypes.func
} : void 0;
TextArea.defaultProps = {
  id: undefined,
  value: undefined,
  label: undefined,
  errors: [],
  callouts: false,
  tooltip: undefined,
  maxLength: undefined,
  rows: DEFAULT_ROWS,
  ariaLabel: undefined,
  ariaRequired: undefined,
  ariaDescribedBy: undefined,
  disabled: false,
  autoFocus: false,
  placeholder: undefined,
  required: false,
  customStyle: {
    rootStyle: {},
    inputAreaStyle: {},
    maxLengthStyle: {}
  },
  onChange: noop
};
process.env.NODE_ENV !== "production" ? TextArea.displayName = 'TextArea' : void 0;
var TextArea$1 = TextArea;

export { TextArea$1 as default };
